// Global wishlist state management
const WishlistState = {
  // Determine if wishlist is enabled and accessible
  get enabled() {
    return theme.config.hasLocalStorage;
  },
  
  // Check if login is required and if user is logged in
  get accessible() {
    if (theme.wishlist.wishlistRequireLogin && !theme.wishlist.customerLoggedIn) {
      return false;
    }
    return true;
  },
  
  // Get customer ID from theme settings
  get customerId() {
    return String(theme.wishlist.customerId || '');
  },
  
  // Check if Gadget API is available
  get hasGadgetApi() {
    return typeof window.api !== 'undefined' && window.api !== null;
  },
  
  // Get all wishlist items (respecting login requirement)
  getItems() {
    if (!this.enabled) return [];
    
    // If login is required but user is not logged in, return empty array for display purposes
    if (theme.wishlist.wishlistRequireLogin && !theme.wishlist.customerLoggedIn) {
      return [];
    }
    
    return JSON.parse(window.localStorage.getItem('wishlist-items') || '[]').map(Number);
  },
  
  // Get all wishlist items regardless of login state (for storage purposes)
  getAllItems() {
    if (!this.enabled) return [];
    return JSON.parse(window.localStorage.getItem('wishlist-items') || '[]').map(Number);
  },
  
  // Check if product is in wishlist (respecting login requirement)
  hasItem(productId) {
    // If login is required but user is not logged in, always return false for UI purposes
    if (theme.wishlist.wishlistRequireLogin && !theme.wishlist.customerLoggedIn) {
      return false;
    }
    
    return this.getAllItems().includes(Number(productId));
  },
  
  // Add item to wishlist
  async addItem(productId) {
    if (!this.enabled || !this.accessible) return false;
    
    const items = this.getAllItems();
    if (!items.includes(Number(productId))) {
      // Add to localStorage first for immediate UI feedback
      items.push(Number(productId));
      window.localStorage.setItem('wishlist-items', JSON.stringify(items));
      
      // If Gadget API and customer ID are available, sync with Shopify metafields
      if (this.hasGadgetApi && this.customerId) {
        try {
          const result = await window.api.manageWishlist({
            action: "add",
            customerId: this.customerId,
            productId: productId.toString(),
          });
        } catch (error) {
          console.error("Error syncing wishlist to Shopify metafields:", error);
        }
      }
      
      return true;
    }
    return false;
  },
  
  // Remove item from wishlist
  async removeItem(productId) {
    if (!this.enabled || !this.accessible) return false;
    
    const items = this.getAllItems();
    const index = items.indexOf(Number(productId));
    
    if (index !== -1) {
      // Remove from localStorage first for immediate UI feedback
      items.splice(index, 1);
      window.localStorage.setItem('wishlist-items', JSON.stringify(items));
      
      // If Gadget API and customer ID are available, sync with Shopify metafields
      if (this.hasGadgetApi && this.customerId) {
        try {
          const result = await window.api.manageWishlist({
            action: "remove",
            customerId: this.customerId,
            productId: productId.toString(),
          });
        } catch (error) {
          console.error("Error syncing wishlist removal to Shopify metafields:", error);
        }
      }
      
      return true;
    }
    return false;
  },
  
  // Clear the entire wishlist
  async clearItems() {
    if (!this.enabled || !this.accessible) return false;
    
    // Clear localStorage first for immediate UI feedback
    window.localStorage.setItem('wishlist-items', '[]');
    
    // If Gadget API and customer ID are available, sync with Shopify metafields
    if (this.hasGadgetApi && this.customerId) {
      try {
        const result = await window.api.manageWishlist({
          action: "reset",
          customerId: this.customerId,
        });
      } catch (error) {
        console.error("Error syncing wishlist reset to Shopify metafields:", error);
      }
    }
    
    return true;
  },
  
  // Initialize by syncing with Shopify metafields if available - do a comparison of local storage and Shopify wishlist items to find the delta. It'll then update the Shopify wishlist items any items found in local storage but not in Shopify and vice versa.
  async initializeFromShopify() {
    if (!this.hasGadgetApi || !this.customerId) return;
    
    try {
      // Get Shopify wishlist items from customer metafield
      const shopifyWishlistItems = theme.wishlist.customerWishlistItems || [];
      
      // Extract product IDs from the Shopify wishlist (full product objects)
      const shopifyProductIds = shopifyWishlistItems.map(product => product.id);
      
      // Get local storage wishlist items (numeric IDs)
      const localStorageItems = this.getAllItems();
      
      // Create Sets for easy lookup and comparison
      const shopifyIdsSet = new Set(shopifyProductIds);
      const localIdsSet = new Set(localStorageItems);

      // Find items that exist in local storage but not in Shopify
      const itemsToAddToShopify = localStorageItems.filter(id => !shopifyIdsSet.has(id));
      
      // Find items that exist in Shopify but not in local storage
      const itemsToAddToLocal = shopifyProductIds.filter(id => !localIdsSet.has(Number(id)));
      
      // Sync items from local storage to Shopify
      for (const productId of itemsToAddToShopify) {
        try {
          await window.api.manageWishlist({
            action: "add",
            customerId: this.customerId,
            productId: productId.toString()
          });
        } catch (error) {
          console.error(`Failed to sync local item ${productId} to Shopify:`, error);
        }
      }
      
      // Sync items from Shopify to local storage
      let localItems = [...localStorageItems];
      let changed = false;
      
      for (const productId of itemsToAddToLocal) {
        if (!localItems.includes(Number(productId))) {
          localItems.push(Number(productId));
          changed = true;
        }
      }
      
      // Update local storage if needed
      if (changed) {
        window.localStorage.setItem('wishlist-items', JSON.stringify(localItems));
      }
      
      // Log sync results
      if (itemsToAddToShopify.length > 0 || itemsToAddToLocal.length > 0) {
        console.log(`Wishlist sync complete. Added ${itemsToAddToShopify.length} items to Shopify and ${itemsToAddToLocal.length} items to local storage.`);
        
        // Update UI for all wishlist buttons on the page
        this.updateButtonsAfterSync();
      }
    } catch (error) {
      console.error("Error initializing wishlist from Shopify:", error);
    }
  },
  
  // Update button states after syncing with Shopify
  updateButtonsAfterSync(productIds) {
    // Update buttons for all wishlist items
    const allButtons = document.querySelectorAll('wishlist-button[data-product-id]');
    
    allButtons.forEach(button => {
      // Force button to re-check its state
      button.updateButtonState();
    });
    
    // Also update any wishlist count elements
    document.querySelectorAll('wishlist-count').forEach(counter => {
      if (typeof counter.updateCount === 'function') {
        counter.updateCount();
      }
    });
  }
};

// Initialize Gadget API if available
document.addEventListener('DOMContentLoaded', function() {
  // Check if Gadget client is already initialized
  if (!window.api && typeof Gadget !== 'undefined') {
    window.api = new Gadget();
  }
  
  // Initialize wishlist from Shopify if customer is logged in
  if (theme.wishlist.customerLoggedIn) {
    WishlistState.initializeFromShopify().then(() => {
      // Once initialization is complete, update all buttons
      WishlistState.updateButtonsAfterSync();
    });
  } else {
    // Customer not logged in, still make sure all buttons are in correct state
    WishlistState.updateButtonsAfterSync();
  }
});

// Listen for login events if available
document.addEventListener('customer:login', function() {
  // Customer just logged in, sync wishlist
  if (WishlistState.hasGadgetApi) {
    WishlistState.initializeFromShopify();
  }
});

class WishlistItems extends HTMLElement {
  constructor() {
    super();

    if (!WishlistState.enabled) {
      this.hidden = true;
      return;
    }

    this.limit = this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit')) : 10;
    this.fetchedProducts = new Map(); // Create a map to store fetched products so we can get the delta
    this.context = this.getAttribute('data-context');
    this.sectionId = this.getAttribute('data-section-id'); // Section ID for Section Rendering API

    // Listen for wishlist changes to reinitialize or re-render section
    const wishlistEventHandler = (event) => {
      if (this.context === 'section' && this.sectionId) {
        this.renderSection();
      } else {
        this.reinitialize();
      }
    };
    this._wishlistEventHandler = wishlistEventHandler; // Store reference for potential removal
    document.addEventListener('wishlist:item-added', wishlistEventHandler);
    document.addEventListener('wishlist:item-removed', wishlistEventHandler);
    document.addEventListener('wishlist:cleared', wishlistEventHandler);

    Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' });
  }

  disconnectedCallback() {
    // Clean up event listeners to prevent memory leaks
    if (this._wishlistEventHandler) {
      document.removeEventListener('wishlist:item-added', this._wishlistEventHandler);
      document.removeEventListener('wishlist:item-removed', this._wishlistEventHandler);
      document.removeEventListener('wishlist:cleared', this._wishlistEventHandler);
    }
  }
  
  reinitialize() {
    // Clear current content
    this.innerHTML = '';
    // Re-initialize the component
    this.init();
  }
  
  handleWishlistCleared() {
    // Clear content and show empty state
    this.innerHTML = '';
    this.dispatchEvent(new CustomEvent('wishlist:cleared'));
  }

  init() {
    // If login is required and user is not logged in, show empty state
    if (!WishlistState.accessible) {
      this.innerHTML = `
      <div class="drawer__scrollable relative flex justify-center items-start grow shrink text-center">
        <div class="drawer__empty grid gap-5 md:gap-8">
          <h2 class="drawer__empty-text heading leading-none tracking-tight">Please log in to view your wishlist</h2>
          <a href="${theme.routes.account_login_url}" class="button button--primary button--md" is="hover-link">
            <span class="btn-fill" data-fill></span>
            <span class="btn-text">Login</span>
          </a>
        </div>
      </div>
      `;
      return;
    }
    
    const allWishlistItems = this.getAllWishlistItems();
    if (allWishlistItems.length === 0) {
      this.dispatchEvent(new CustomEvent('wishlist:cleared'));
      return;
    }
    
    // Fetch wishlist items in batches
    if (this.context !== 'section') {
      this.fetchProductBatch(allWishlistItems);
    } 
  }
  
  fetchProductBatch(items, startIndex = 0) {
    const batchSize = this.limit;
    const endIndex = Math.min(startIndex + batchSize, items.length);
    const currentBatch = items.slice(startIndex, endIndex);
    
    if (currentBatch.length === 0) {
      // All items have been fetched, bail out. 
      return;
    }
    
    const queryString = currentBatch.map(item => `id:${item}`).join(' OR ');
    
    fetch(this.getAttribute('data-url') + queryString)
      .then(response => response.text())
      .then(responseText => {
        const sectionInnerHTML = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .querySelector('.shopify-section');

        const recommendations = sectionInnerHTML.querySelector('wishlist-items');
        if (recommendations && recommendations.innerHTML.trim().length) {
          // For the first batch, set the HTML directly
          if (startIndex === 0) {
          this.innerHTML = recommendations.innerHTML;
          } else {
            // For subsequent batches, append to the existing list
            const productCards = recommendations.querySelectorAll('.horizontal-product');
            const container = this.querySelector('.horizontal-products');
            
            if (container && productCards.length > 0) {
              productCards.forEach(card => {
                container.appendChild(card.cloneNode(true));
              });
            }
          }
          
          this.dispatchEvent(new CustomEvent('wishlistItems:batchLoaded', { 
            detail: { startIndex, endIndex }
          }));
          
          // Fetch next batch
          if (endIndex < items.length) {
            this.fetchProductBatch(items, endIndex);
          } else {
          this.dispatchEvent(new CustomEvent('wishlistItems:loaded'));
        }
        }
        else if (startIndex === 0) {
          // If the first batch is empty, the entire wishlist is empty
          this.closest('.wishlist-section')?.remove();
          this.dispatchEvent(new CustomEvent('wishlist:cleared'));
        }
      })
      .catch(e => {
        console.error('Error fetching wishlist batch:', e);
        
        // Try to fetch next batch even if this one failed
        if (endIndex < items.length) {
          this.fetchProductBatch(items, endIndex);
        }
      });
  }

  getAllWishlistItems() {
    if (!WishlistState.enabled) {
      return [];
    }
    
    // If login is required and user is not logged in, return empty array
    if (!WishlistState.accessible) {
      return [];
    }
    
    const wishlistItems = window.localStorage.getItem('wishlist-items') || '[]';
    const items = JSON.parse(wishlistItems).map(Number);
    
    // Reverse the array to show newest added items first
    return items.reverse();
  }

  getQueryString() {
    let wishlistItems = '[]';
    if (theme.config.hasLocalStorage) {
      wishlistItems = window.localStorage.getItem(`wishlist-items`) || '[]';
    }

    const items = new Set(JSON.parse(wishlistItems));
    if (this.hasAttribute('data-product-id')) {
      items.delete(parseInt(this.getAttribute('data-product-id')));
    }
    return Array.from(items.values(), (item) => `id:${item}`).slice(0, this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit')) : 4).join(' OR ');
  }

  /**
   * Uses Shopify's Section Rendering API to re-render this section.
   * Requires data-section-id attribute to be set on the element.
   * Will replace the outerHTML of this section with the new HTML from the API.
   */
  renderSection() {
    if (!this.sectionId) return;
    // Optionally, you can add a loading state here
    fetch(`${window.location.pathname}?section_id=${this.sectionId}`)
      .then((response) => response.text())
      .then((html) => {
        // Parse the returned HTML and replace the section
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newSection = doc.querySelector(`[data-section-id="${this.sectionId}"]`);
        if (newSection && this.parentNode) {
          this.parentNode.replaceChild(newSection, this);
        }
      })
      .catch((e) => {
        console.error('Error re-rendering wishlist section:', e);
      });
  }
}

customElements.define('wishlist-items', WishlistItems);

class WishlistButton extends HTMLElement {
  constructor() {
    super();

    if (!WishlistState.enabled) {
      this.hidden = true;
      return;
    }
  }

  connectedCallback() {
    // Set the initial state based on localStorage and login state
    this.updateButtonState();
    
    const button = this.querySelector('button, a');
    if (button) {
      button.addEventListener('click', this.onWishlistButtonClick.bind(this));
    }

    // Listen for wishlist changes to update button state
    this._boundHandleWishlistEvent = this.handleWishlistEvent.bind(this);
    ['wishlist:item-added', 'wishlist:item-removed', 'wishlist:cleared'].forEach(eventType => {
      document.addEventListener(eventType, this._boundHandleWishlistEvent);
    });
  }

  disconnectedCallback() {
    // Clean up event listeners to prevent memory leaks
    if (this._boundHandleWishlistEvent) {
      ['wishlist:item-added', 'wishlist:item-removed', 'wishlist:cleared'].forEach(eventType => {
        document.removeEventListener(eventType, this._boundHandleWishlistEvent);
      });
    }
  }

  get productId() {
    return parseInt(this.getAttribute('data-product-id'));
  }

  get action() {
    return this.getAttribute('data-action');
  }

  set action(value) {
    this.setAttribute('data-action', value);
  }

  handleWishlistEvent(event) {
    // Don't update clear or open buttons
    if (this.action === 'clear' || this.action === 'open') return;
    
    // Don't update the button that triggered the event (it already updated itself)
    if (event.target === this) return;
    
    // For cleared events, reset all buttons to 'add' state
    if (event.type === 'wishlist:cleared') {
      this.updateButtonState('add');
      return;
    }
    
    // For add/remove events, check if this button's product was affected
    const eventProductId = event.detail?.productAdded || event.detail?.productRemoved;
    if (eventProductId && eventProductId === this.productId) {
      // Update button state based on current wishlist state
      this.updateButtonState();
    }
  }

  onWishlistButtonClick(event) {
    event.preventDefault();
    
    // Special case for 'open' action - always allow this regardless of login state
    if (this.action === 'open') {
      this.openWishlist();
      return;
    }
    
    // Check if login is required and user is not logged in
    if (!WishlistState.accessible) {
      this.updateButtonState('add');
      return;
    }
    
    // Show loading state
    const button = this.querySelector('button, a');
    
    switch (this.action) {
      case 'add':
        this.addToWishlist();
        break;
        
      case 'remove':
        this.removeFromWishlist();
        break;
        
      case 'clear':
        this.clearWishlist();
        break;
        
      default:
        console.warn(`Unknown wishlist action: ${this.action}`);
        break;
    }
  }
  
  async addToWishlist() {
    // Store current state to revert if needed
    const previousState = this.getAttribute('data-wishlist-state');
    
    // Instant feedback
    this.updateButtonState('remove');

    try {
      if (await WishlistState.addItem(this.productId)) {
        // Dispatch event with count in the detail
        this.dispatchEvent(new CustomEvent('wishlist:item-added', {
          bubbles: true, 
          detail: { 
            wishlistAction: 'add',
            updateWishlist: true,
            productAdded: this.productId,
            count: WishlistState.getItems().length
          }
        }));
      } else {
        // If addItem returns false, revert to previous state
        this.updateButtonState(previousState);
      }
    } catch (error) {
      console.error('Error adding item to wishlist:', error);
      // Revert to previous state on error
      this.updateButtonState(previousState);
    }
  }
  
  async removeFromWishlist() {
    // Store current state to revert if needed
    const previousState = this.getAttribute('data-wishlist-state');
    const context = this.getAttribute('data-context');
    
    // Instant feedback
    this.updateButtonState('add');

    if (context === 'wishlist-drawer') {
      this.querySelector('button, a').classList.add('is-loading');
    }

    try {
      if (await WishlistState.removeItem(this.productId)) {
        // Dispatch event with count in the detail
        this.dispatchEvent(new CustomEvent('wishlist:item-removed', {
          bubbles: true, 
          detail: { 
            wishlistAction: 'remove',
            updateWishlist: true,
            productRemoved: this.productId,
            count: WishlistState.getItems().length
          }
        }));
      } else {
        // If removeItem returns false, revert to previous state
        this.updateButtonState(previousState);
      }
    } catch (error) {
      console.error('Error removing item from wishlist:', error);
      // Revert to previous state on error
      this.updateButtonState(previousState);
    }

    if (context === 'wishlist-drawer') {
      this.querySelector('button, a').classList.remove('is-loading');
    }
  }

  async clearWishlist() {
    try {
      if (await WishlistState.clearItems()) {
        // Dispatch event with count=0 in the detail
        this.dispatchEvent(new CustomEvent('wishlist:cleared', {
          bubbles: true, 
          detail: { 
            updateWishlist: true,
            wishlistAction: 'clear',
            count: 0
          }
        }));
        
        // Update all wishlist buttons to "add" state
        this.resetAllWishlistButtons();
        
        // Optionally show a confirmation message
        const button = this.querySelector('button, a');
        if (button) {
          const originalText = button.textContent || 'Clear Wishlist';
          button.textContent = 'Wishlist Cleared!';
          setTimeout(() => {
            button.textContent = originalText;
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Error clearing wishlist:', error);
    } finally {
      // Remove loading state
      const button = this.querySelector('button, a');
      if (button) {
        button.classList.remove('is-loading');
      }
    }
  }
  
  resetAllWishlistButtons() {
    // Find all wishlist-button elements with data-action="remove"
    const wishlistButtons = document.querySelectorAll('wishlist-button[data-action="remove"]');
    
    wishlistButtons.forEach(button => {
      button.setAttribute('data-action', 'add');
      if (typeof button.updateButtonState === 'function') {
        button.updateButtonState('add');
      }
    });
  }

  updateButtonState(forceAction = null) {
    const button = this;
    if (!button) return;
    
    // Don't update clear or open buttons
    if (this.action === 'clear' || this.action === 'open') return;
    
    if (forceAction) {
      button.setAttribute('data-action', forceAction);
      this.action = forceAction;
      
      if (forceAction === 'remove') {
        button.setAttribute('aria-label', 'Remove from wishlist');
      } else {
        button.setAttribute('aria-label', 'Add to wishlist');
      }
      return;
    }
    
    // Check if this product is already in wishlist
    if (WishlistState.hasItem(this.productId)) {
      button.setAttribute('data-action', 'remove');
      this.action = 'remove';
      button.setAttribute('aria-label', 'Remove from wishlist');
    } else {
      button.setAttribute('data-action', 'add');
      this.action = 'add';
      button.setAttribute('aria-label', 'Add to wishlist');
    }
  }

  openWishlist() {
    // Dispatch event to open cart drawer with wishlist tab
    document.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true, 
      detail: { 
        tab: 'wishlist',
        open: true
      }
    }));
    
    // Fetch and update wishlist items
    const wishlistItemsElement = document.querySelector('wishlist-items');
    if (wishlistItemsElement) {
      wishlistItemsElement.reinitialize();
    }
  }
}

customElements.define('wishlist-button', WishlistButton);

class WishlistCount extends HTMLElement {
  constructor() {
    super();
    
    if (!WishlistState.enabled) {
      this.hidden = true;
      return;
    }
  }

  connectedCallback() {
    // Initialize count on connection
    this.updateCount();
            
    // Listen for wishlist changes with count data
    ['wishlist:item-added', 'wishlist:item-removed', 'wishlist:cleared'].forEach(eventType => {
      document.addEventListener(eventType, this.handleWishlistEvent.bind(this));
    });
  }
  
  handleWishlistEvent(event) {
    // Use the count from the event detail if available
    if (event.detail && typeof event.detail.count === 'number') {
      this.updateCountValue(event.detail.count);
    } else {
      // Fallback to recalculating from localStorage
      this.updateCount();
    }
  }
  
  updateCount() {
    const count = WishlistState.getItems().length;
    this.updateCountValue(count);
  }
  
  updateCountValue(count) {
    this.textContent = count;
    this.setAttribute('aria-label', `${count} ${count === 1 ? 'item' : 'items'} in wishlist`);
  }
}

customElements.define('wishlist-count', WishlistCount);